import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/auth';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import NotificationBell from './NotificationBell';
import BrandLogo from '../BrandLogo';

/**
 * Header component for the application
 * Includes navigation and user controls
 */
const Header = ({ onOpenNav }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const navigate = useNavigate();
  const isLoggedIn = authService.isLoggedIn();
  
  // Debug: Log authentication state
  const debugAuth = process.env.NODE_ENV === 'development';

  // Derive a safe display name and initials from the fetched profile
  const getDisplayName = () => {
    const name = (userProfile?.username || '').trim();
    if (name) return name;
    const email = (userProfile?.email || '').trim();
    if (email && email.includes('@')) return email.split('@')[0];
    return 'User';
  };

  const getInitials = () => {
    const dn = getDisplayName();
    return dn.substring(0, 2).toUpperCase();
  };

  /**
   * Fetch user profile data when component mounts
   */
  useEffect(() => {
    const fetchUserProfile = async () => {
      // Check if we have a token in localStorage
      const userStr = localStorage.getItem('user');

      
      if (userStr && !userProfile) {
        console.log('Header: Found token, fetching user profile...');
        setIsLoadingProfile(true);

        try {
          const profile = await authService.fetchCurrentUserProfile();

          setUserProfile(profile);
        } catch (error) {
          console.error('Header: Failed to fetch profile, using token data');
          // Fall back to decoding the JWT payload for basic info
          try {
            const tokenData = JSON.parse(userStr);
            const payload = JSON.parse(atob(tokenData.access_token.split('.')[1]));
            const fallback = {
              username: payload.sub || 'User',
              email: '',
              company_name: '',
              role: payload.role || 'user',
              id: 0,
              is_active: true
            };
            setUserProfile(fallback);
          } catch {
            setUserProfile({ username: 'User', email: '', company_name: '', role: 'user', id: 0, is_active: true });
          }
        } finally {
          setIsLoadingProfile(false);
        }
      } else if (!userStr) {
        console.log('Header: No token found, user not logged in');
      } else if (userProfile) {
        if (debugAuth) console.debug('Profile already loaded');
      }
    };

    fetchUserProfile();
  }, [userProfile, navigate]); // Check on every render until profile is loaded

  /**
   * Handle user logout
   */
  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  // Debug function to manually test profile fetch
  const debugFetchProfile = async () => {
    console.log('=== DEBUG PROFILE FETCH ===');
    console.log('isLoggedIn:', authService.isLoggedIn());
    if (debugAuth) console.debug('Profile refresh requested');
    
    try {
      const profile = await authService.fetchCurrentUserProfile();
      if (debugAuth) console.debug('Profile refreshed');
      setUserProfile(profile);
    } catch (error) {
      console.error('Profile fetch failed');
    }
  };

  /**
   * Toggle mobile menu
   */
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  /**
   * Toggle profile dropdown menu
   */
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  return (
    <header className="bg-gray-900 shadow-lg fixed top-0 w-full z-50 border-b border-gray-800">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between items-center">
          <div className="flex items-center">
            {/* Mobile navigation toggle — opens the off-canvas sidebar drawer.
                Only for authenticated app pages (Layout passes onOpenNav then). */}
            {isLoggedIn && onOpenNav && (
              <button
                type="button"
                onClick={onOpenNav}
                className="md:hidden mr-1 inline-flex items-center justify-center rounded-md p-2 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-label="Open navigation menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div className="flex flex-shrink-0 items-center">
              <Link to="/" className="flex items-center gap-2.5">
                {/* Signature gradient brand mark */}
                <span className="md:hidden"><BrandLogo compact /></span>
                <span className="hidden md:inline-flex"><BrandLogo /></span>
              </Link>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {isLoggedIn ? (
              <div className="flex items-center space-x-4">
                {/* Notifications */}
                <NotificationBell />
                
                {/* User Profile Menu */}
                <div className="relative">
                  <div>
                    <button
                      type="button"
                      className="flex items-center space-x-2 text-sm focus:outline-none"
                      id="user-menu-button"
                      aria-expanded={isProfileMenuOpen}
                      aria-haspopup="true"
                      onClick={toggleProfileMenu}
                    >
                      <span className="sr-only">Open user menu</span>
                      <div className="h-8 w-8 rounded-full bg-gray-700 ring-2 ring-blue-500 flex items-center justify-center text-white overflow-hidden">
                        <span className="text-sm font-medium">{getInitials()}</span>
                      </div>
                      <div className="hidden md:block text-left">
                        <div className="text-sm font-medium text-white">
                          {isLoadingProfile ? 'Loading...' : getDisplayName()}
                        </div>
                        <div className="text-xs text-gray-400">
                          {userProfile?.role ? userProfile.role.charAt(0).toUpperCase() + userProfile.role.slice(1) : 'User'}
                        </div>
                      </div>
                    </button>
                  </div>
                
                  {isProfileMenuOpen && (
                    <div
                      className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl sm-glass py-1 shadow-2xl ring-1 ring-white/10 focus:outline-none"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                    >
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                        role="menuitem"
                      >
                        Your Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                        role="menuitem"
                      >
                        Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 hover:text-red-300"
                        role="menuitem"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 shadow-sm hover:bg-gray-700"
                >
                  Sign up
                </Link>
              </div>
            )}
          </div>
          
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              aria-controls="mobile-menu"
              aria-expanded={isMenuOpen}
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden`}
        id="mobile-menu"
      >
        {isLoggedIn ? (
          <div className="border-t border-gray-700 pt-4 pb-3 bg-gray-900">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-indigo-700 flex items-center justify-center text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-white">
                  {isLoadingProfile ? 'Loading...' : getDisplayName()}
                </div>
                <div className="text-sm text-gray-400">
                  {userProfile?.role ? userProfile.role.charAt(0).toUpperCase() + userProfile.role.slice(1) : 'User'}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1 px-2">
              <Link
                to="/settings"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                Your Profile
              </Link>
              <Link
                to="/settings"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                Settings
              </Link>
              {/* Prominent Sign Out button in mobile menu */}
              <button
                onClick={handleLogout}
                className="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-red-400 hover:bg-gray-700 hover:text-red-300"
              >
                Sign Out
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-700 pt-4 pb-3 bg-gray-900">
            <div className="flex flex-col space-y-2 px-4">
              <Link
                to="/login"
                className="block rounded-md bg-indigo-600 px-4 py-2 text-center text-base font-medium text-white hover:bg-indigo-700"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="block rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-center text-base font-medium text-gray-300 hover:bg-gray-700"
              >
                Sign up
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
